import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  FaWifi,
  FaSwimmer,
  FaParking,
  FaUtensils,
  FaStar,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../Config/config";
import Loading from "../../loading";
import Navbar from "../../Components/Navbar";

const HotelPage = () => {
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHotel = async () => {
      try {
        const hotelDoc = doc(db, "hotels", id);
        const hotelSnap = await getDoc(hotelDoc);
        if (hotelSnap.exists()) {
          const hotelData = { id: hotelSnap.id, ...hotelSnap.data() };
          setHotel(hotelData);
          setSelectedImage(hotelData.imagesArray?.[0] || "https://via.placeholder.com/800x600");
        } else {
          console.log("No such hotel!");
        }
      } catch (error) {
        console.error("Error fetching hotel:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotel();
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [id]);

  const amenityIcons = {
    wifi: FaWifi,
    pool: FaSwimmer,
    parking: FaParking,
    breakfast: FaUtensils,
  };

  const amenityColors = {
    wifi: "#3498db",
    pool: "#9b59b6",
    parking: "#e74c3c",
    breakfast: "#2ecc71",
  };

  if (isLoading) return <Loading />;
  if (!hotel) return <div>Hotel not found</div>;

  const availableAmenities = hotel.amenitiesArray 
    ? Object.entries(hotel.amenitiesArray).filter(([_, isAvailable]) => isAvailable)
    : [];

  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <ImageSection>
          <MainImage src={selectedImage}>
            <Overlay />
          </MainImage>
          {hotel.imagesArray && hotel.imagesArray.length > 0 && (
            <ThumbnailsContainer>
              <Thumbnails>
                {hotel.imagesArray.map((thumb, index) => (
                  <Thumbnail
                    key={index}
                    src={thumb}
                    alt={`thumbnail-${index}`}
                    onClick={() => setSelectedImage(thumb)}
                    isSelected={thumb === selectedImage}
                  />
                ))}
              </Thumbnails>
            </ThumbnailsContainer>
          )}
        </ImageSection>
        <InfoSection>
          <HotelTitle>{hotel.hotelName}</HotelTitle>
          <Rating>
            <FaStar /> {hotel.hotelStars} Star Hotel
          </Rating>
          <Location>{hotel.hotelLocation || "Location not specified"}</Location>
          <Description>
            {hotel.description || "This hotel offers a range of amenities and services to ensure a comfortable stay."}
          </Description>
          {availableAmenities.length > 0 && (
            <AmenitiesContainer>
              {availableAmenities.map(([amenity]) => {
                const Icon = amenityIcons[amenity];
                return Icon ? (
                  <AmenityItem
                    key={amenity}
                    color={amenityColors[amenity]}
                  >
                    <Icon size={20} />
                    <AmenityName>{amenity}</AmenityName>
                  </AmenityItem>
                ) : null;
              })}
            </AmenitiesContainer>
          )}
          <MembershipList>
            <MembershipItem>✓ Membership Benefits Available</MembershipItem>
          </MembershipList>
        </InfoSection>
      </ContentContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  font-family: "Poppins", sans-serif;
  max-width: 100%;
  margin: 10vh 0 0 0;
  
  @media (max-width: 768px) {
    margin: 17vh 0 10vh 0; /* Added 5vh to top and bottom for mobile */
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ImageSection = styled.div`
  width: 100%;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const MainImage = styled.div`
  width: 100%;
  padding-top: 75%;
  background: url(${(props) => props.src}) center center/cover no-repeat;
  border-radius: 12px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding-top: 66.67%;
    border-radius: 10px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1),
    rgba(0, 0, 0, 0.5)
  );
`;

const ThumbnailsContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    margin-top: 8px;
  }
`;

const Thumbnails = styled.div`
  display: inline-flex;
  padding: 0 8px;
`;

const Thumbnail = styled.img`
  width: 80px;
  height: 60px;
  border-radius: 8px;
  margin: 0 8px;
  cursor: pointer;
  border: 3px solid ${(props) => (props.isSelected ? "#441B77" : "transparent")};
  transition: all 0.3s ease;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 50px;
    height: 38px;
    margin: 0 4px;
    border-width: 2px;
    border-radius: 6px;
  }
`;

const InfoSection = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const HotelTitle = styled.h2`
  font-size: 2.5em;
  color: #2c3e50;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 1.5em;
    margin-bottom: 10px;
    line-height: 1.2;
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.3em;
  color: #f39c12;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 1em;
    margin-bottom: 10px;
  }
`;

const Location = styled.p`
  color: #7f8c8d;
  font-size: 1.1em;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 0.9em;
    margin-bottom: 12px;
  }
`;

const Description = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  color: #34495e;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 0.9em;
    line-height: 1.5;
    margin-bottom: 16px;
  }
`;

const AmenitiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const AmenityItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.color || "#f0f0f0"};
  padding: 12px;
  border-radius: 10px;
  color: white;
  width: 70px;
  height: 70px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    padding: 8px;
    border-radius: 8px;
  }

  &:hover {
    transform: translateY(-3px);
  }
`;

const AmenityName = styled.span`
  margin-top: 6px;
  font-size: 0.9em;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.75em;
    margin-top: 4px;
  }
`;

const MembershipList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const MembershipItem = styled.li`
  color: #27ae60;
  margin-bottom: 8px;
  font-size: 1.1em;
  
  @media (max-width: 768px) {
    font-size: 0.9em;
    margin-bottom: 6px;
  }
`;

export default HotelPage;